// Wire-Format zwischen api und web (und worker, wo nötig). Browser-tauglich: kein Node-Import.
import { z } from 'zod';

export const AddressSchema = z.object({ name: z.string().optional(), address: z.string() });
export type Address = z.infer<typeof AddressSchema>;

export const MAILBOX_COLORS = ['green', 'magenta', 'sky', 'blue', 'red', 'yellow'] as const;
export type MailboxColor = (typeof MAILBOX_COLORS)[number];

export const TEAM = {
  till: { name: 'Till', color: '#4F46E5' },
  lars: { name: 'Lars', color: '#2E9E5B' },
  maren: { name: 'Maren', color: '#C9662E' },
} as const;
export type UserKey = keyof typeof TEAM;
export const isUserKey = (u: unknown): u is UserKey => typeof u === 'string' && u in TEAM;

export const MailboxSchema = z.object({
  id: z.number(),
  address: z.string(),
  displayName: z.string(),
  company: z.enum(['dbdm', 'hoerthin']),
  color: z.string(),
  visibility: z.enum(['shared', 'personal']),
  owner: z.string().nullable(),
  bobEnabled: z.boolean(),
  syncEnabled: z.boolean(),
  lastSyncAt: z.string().nullable(),
  syncError: z.string().nullable(),
  unread: z.number(),
  inboxOpen: z.number(),
  // env: Zugangsdaten aus der Server-Umgebung (die sechs Bestandspostfächer, nur dort änderbar);
  // db: in den Einstellungen angelegt, Passwort verschlüsselt in der Datenbank (D-060)
  credentials: z.enum(['env', 'db']),
  createdBy: z.string().nullable(),
  imapHost: z.string(),
  smtpHost: z.string(),
  // darf diese Person Anzeigename/Farbe ändern, das Passwort erneuern oder das Postfach entfernen?
  editable: z.boolean(),
});
export type Mailbox = z.infer<typeof MailboxSchema>;

// Einstellungen → „Postfach hinzufügen“ (API und Formular prüfen mit demselben Schema)
const hostSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
    'Servername ungültig',
  );
const portSchema = z.number().int().min(1).max(65535);
export const MailboxCreateSchema = z.object({
  address: z.string().trim().toLowerCase().email('E-Mail-Adresse ungültig').max(120),
  displayName: z.string().trim().min(1, 'Anzeigename fehlt').max(80),
  company: z.enum(['dbdm', 'hoerthin']),
  color: z.enum(MAILBOX_COLORS),
  visibility: z.enum(['shared', 'personal']),
  password: z.string().min(1, 'Passwort fehlt').max(200),
  imapUser: z.string().trim().min(1).max(120).optional(), // Standard: die Adresse
  imapHost: hostSchema.optional(),
  imapPort: portSchema.optional(),
  smtpHost: hostSchema.optional(),
  smtpPort: portSchema.optional(),
  smtpUser: z.string().trim().min(1).max(120).optional(),
  smtpPassword: z.string().min(1).max(200).optional(),
  // „alle“, ein Datum (JJJJ-MM-TT) oder leer (Standard: 12 Monate), siehe D-057
  syncSince: z
    .string()
    .trim()
    .regex(/^(alle|\d{4}-\d{2}-\d{2})?$/i, 'Datum als JJJJ-MM-TT oder „alle“')
    .optional(),
});
export type MailboxCreate = z.infer<typeof MailboxCreateSchema>;

export const MailboxPatchSchema = z
  .object({
    displayName: z.string().trim().min(1).max(80),
    color: z.enum(MAILBOX_COLORS),
    visibility: z.enum(['shared', 'personal']),
    password: z.string().min(1).max(200),
    imapUser: z.string().trim().min(1).max(120),
  })
  .partial();
export type MailboxPatch = z.infer<typeof MailboxPatchSchema>;

export const VIEWS = [
  'inbox',
  'mine',
  'pinned',
  'waiting',
  'info',
  'junk',
  'later',
  'done',
  'all',
] as const;
export type View = (typeof VIEWS)[number];
export const VIEW_LABELS: Record<View, string> = {
  inbox: 'Posteingang',
  mine: 'Mir zugewiesen',
  pinned: 'Angeheftet',
  waiting: 'Wartet auf Antwort',
  info: 'Info & System',
  junk: 'Junk',
  later: 'Später',
  done: 'Heute erledigt',
  all: 'Archiv',
};

export const ThreadStatus = z.enum(['open', 'done', 'later']);
export const Category = z.enum(['wichtig', 'info', 'junk']);
export type Category = z.infer<typeof Category>;

export const ThreadListItemSchema = z.object({
  id: z.number(),
  mailboxId: z.number(),
  subject: z.string(),
  snippet: z.string(),
  participants: z.array(AddressSchema),
  lastFrom: AddressSchema.nullable(), // letzte eingegangene Absenderin (Avatar, Name in der Liste)
  lastDate: z.string().nullable(),
  messageCount: z.number(),
  unreadCount: z.number(),
  hasAttachments: z.boolean(),
  answered: z.boolean(),
  status: ThreadStatus,
  statusAt: z.string().nullable(),
  statusBy: z.string().nullable(),
  snoozeUntil: z.string().nullable(),
  category: Category,
  categoryReason: z.string().nullable(),
  assignedTo: z.string().nullable(),
  waitUntil: z.string().nullable(),
  waitDays: z.number(),
  nachfassDraftId: z.string().nullable(),
  priority: z.number(),
  priorityReason: z.string().nullable(),
  pinned: z.boolean(),
  crmInstitutionId: z.string().nullable(),
  crmInstitutionName: z.string().nullable(),
  crmRole: z.string().nullable(),
  summary: z.string().nullable(),
  headline: z.string().optional(), // Treffer-Ausschnitt bei der Suche
});
export type ThreadListItem = z.infer<typeof ThreadListItemSchema>;

export const AttachmentSchema = z.object({
  // null = Datei liegt nicht auf der Platte, wird beim Öffnen aus IMAP geholt (D-059); fehlt bei Entwürfen
  storagePath: z.string().nullable().optional(),
  id: z.number(),
  filename: z.string(),
  mime: z.string(),
  size: z.number(),
  inline: z.boolean(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

export const MessageSchema = z.object({
  id: z.number(),
  threadId: z.number().nullable(),
  folder: z.string(),
  messageId: z.string(),
  subject: z.string(),
  from: AddressSchema,
  to: z.array(AddressSchema),
  cc: z.array(AddressSchema),
  replyTo: z.array(AddressSchema),
  date: z.string(),
  seen: z.boolean(),
  flagged: z.boolean(),
  direction: z.enum(['in', 'out']),
  snippet: z.string(),
  textBody: z.string().nullable(),
  hasHtml: z.boolean(),
  category: Category,
  categoryReason: z.string().nullable(),
  // Anliegen der Mail als Anweisungen für die Antwort (Chips über dem Editor)
  anliegen: z.array(z.string()).default([]),
  attachments: z.array(AttachmentSchema),
});
export type Message = z.infer<typeof MessageSchema>;

export const RegelLaufSchema = z.object({
  id: z.number(),
  messageId: z.number().nullable(),
  ruleName: z.string(),
  ergebnis: z.string(),
  aktionen: z.array(z.object({ typ: z.string(), ok: z.boolean(), info: z.string().optional() })),
  am: z.string(),
});
export type RegelLauf = z.infer<typeof RegelLaufSchema>;

// Endgültig gescheiterter Sendebefehl zu diesem Thread (z. B. SMTP nicht erreichbar) – mit „Erneut senden“
export const VersandFehlerSchema = z.object({
  commandId: z.number(),
  draftId: z.string().nullable(),
  subject: z.string().nullable(),
  an: z.string().nullable(),
  error: z.string().nullable(),
  am: z.string(),
});
export type VersandFehler = z.infer<typeof VersandFehlerSchema>;

export const ThreadDetailSchema = z.object({
  thread: ThreadListItemSchema,
  messages: z.array(MessageSchema),
  regeln: z.array(RegelLaufSchema).default([]),
  versandFehler: z.array(VersandFehlerSchema).default([]),
  vorschlag: z
    .object({ draftId: z.string(), user: z.string(), anweisung: z.string().nullable() })
    .nullable()
    .default(null),
  // Eigener offener Entwurf (manuell/Bob) zu diesem Thread – wird beim Öffnen wieder aufgemacht
  meinEntwurf: z.object({ draftId: z.string(), updatedAt: z.string() }).nullable().default(null),
});
export type ThreadDetail = z.infer<typeof ThreadDetailSchema>;

export const CountsSchema = z.object({
  views: z.record(z.string(), z.number()),
  mailboxes: z.record(z.string(), z.object({ unread: z.number(), open: z.number() })),
  doneToday: z.number(),
  open: z.number(),
  sorted: z.object({ info: z.number(), junk: z.number() }),
});
export type Counts = z.infer<typeof CountsSchema>;

export const DraftSchema = z.object({
  id: z.string(),
  mailboxId: z.number(),
  threadId: z.number().nullable(),
  to: z.array(AddressSchema),
  cc: z.array(AddressSchema),
  bcc: z.array(AddressSchema),
  subject: z.string(),
  html: z.string(),
  inReplyTo: z.string().nullable(),
  attachmentRefs: z.array(z.number()),
  quelle: z.string(),
  status: z.string(),
  updatedAt: z.string(),
});
export type Draft = z.infer<typeof DraftSchema>;

export const SignatureSchema = z.object({
  id: z.number(),
  mailboxId: z.number(),
  name: z.string(),
  html: z.string(),
});
export type Signature = z.infer<typeof SignatureSchema>;

export const MeSchema = z.object({
  key: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
});
export type Me = z.infer<typeof MeSchema>;

// Bob
export const BobStepSchema = z.object({ tool: z.string(), input: z.unknown(), ok: z.boolean() });
export const BobKarteSchema = z
  .object({
    id: z.string(),
    typ: z.string(),
    token: z.string(),
    titel: z.string().optional(),
    knopf: z.string().optional(),
  })
  .loose();
export type BobKarte = z.infer<typeof BobKarteSchema>;
export const BobResultSchema = z.object({
  ok: z.boolean(),
  antwort: z.string(),
  schritte: z.array(BobStepSchema),
  karten: z.array(BobKarteSchema),
  draftId: z.string().nullable(),
  html: z.string().nullable(),
  pruefen: z.number(),
  kostenEur: z.number(),
  error: z.string().optional(),
});
export type BobResult = z.infer<typeof BobResultSchema>;

// SSE-Ereignisse (api → web)
export type ServerEvent =
  | { kind: 'hello' }
  | { kind: 'threads.changed'; mailboxId: number; threadIds: number[] }
  | { kind: 'mailbox.synced'; mailboxId: number }
  | { kind: 'draft.sent'; draftId: string; threadId: number | null }
  | {
      kind: 'command.failed'; // Befehl endgültig gescheitert (nach allen Versuchen) – z. B. Senden
      mailboxId: number;
      threadId: number | null;
      commandId: number;
      type: string;
      subject: string | null;
      error: string;
    }
  | { kind: 'presence'; threadId: number; mailboxId: number; user: string; editing: boolean } // „Maren schreibt gerade“
  | {
      kind: 'message.new'; // neue eingegangene Mail (wichtig) – für Push und Hinweise
      mailboxId: number;
      threadId: number | null;
      messageId: number;
      from: string;
      subject: string;
    };

// Betreff ohne AW:/Re:/WG:-Präfixe (für Antworten und Anzeige)
export function stripSubjectPrefix(s: string): string {
  let out = s.trim();
  for (;;) {
    const n = out
      .replace(/^((re|aw|antw|antwort|wg|fw|fwd|wtr|sv)(\s*\[\d+\])?\s*:\s*)+/i, '')
      .trim();
    if (n === out) break;
    out = n;
  }
  return out;
}

export const PRUEFEN_RE = /\[PRÜFEN:[^\]]*\]/g;

export { MailKontextSchema, type MailKontext } from './crm.ts';
export * from './suche.ts';
export * from './regeln.ts';
export * from './regeln-engine.ts';
export * from './bausteine.ts';
export * from './markdown.ts';
